export type BlogCourseId =
  | 'hokatsu-start'
  | 'nursery-choice'
  | 'home-monte-intro'
  | 'age2-jibunde'
  | 'toilet-training'
  | 'sleep-bedtime'
  | 'baby-following'
  | 'iyaiya-tantrum'
  | 'parent-care';

export type BlogCourseStep = {
  slug: string;
  bridgeCopy: string;
};

export type BlogCourse = {
  id: BlogCourseId;
  name: string;
  description: string;
  // 複数記事を順番に読むことで体系的に理解できることを示す一言。「Aから Bまで、体系的に」の型で統一する。
  kicker: string;
  nextCourseId?: BlogCourseId;
  steps: BlogCourseStep[];
};

export const BLOG_COURSES: Record<BlogCourseId, BlogCourse> = {
  'hokatsu-start': {
    id: 'hokatsu-start',
    name: '保活スタートコース',
    description: '何から始めればいいか分からない保活の第一歩を、スケジュールと探し方から順番に整理します。',
    kicker: 'スケジュールから探し方まで、体系的に',
    nextCourseId: 'nursery-choice',
    steps: [
      {
        slug: 'hokatsu-itsukara',
        bridgeCopy: 'まずは保活全体のスケジュール感を掴み、いつから動くべきかを押さえましょう。',
      },
      {
        slug: 'hoikuen-sagashikata',
        bridgeCopy: 'スケジュールが分かったら、次は候補園の見つけ方から見学予約までの具体的な手順に進みましょう。',
      },
      {
        slug: 'hoikuen-moushikomi-itsukara',
        bridgeCopy: '候補園が見えてきたら、次は申し込み時期と必要書類を確認して、申請の準備を進めましょう。',
      },
      {
        slug: 'nursery-rejection-what-to-do',
        bridgeCopy:
          '申し込みまで進んだら、万が一不承諾になったときの選択肢も知っておくと、保活全体の不安が和らぎます。',
      },
    ],
  },
  'nursery-choice': {
    id: 'nursery-choice',
    name: '保育園選び・見学コース',
    description: '見学で聞くことから園選びの判断基準まで、後悔しない園選びを一気通貫でサポートします。',
    kicker: '見学の準備から選び方まで、体系的に',
    steps: [
      {
        slug: 'nursery-visit-outfit',
        bridgeCopy: 'まずは見学当日の服装・持ち物を整えて、安心して園に向かえる準備から始めましょう。',
      },
      {
        slug: 'nursery-visit-questions',
        bridgeCopy: '当日の準備が整ったら、次は見学で聞くべき質問リストを押さえましょう。',
      },
      {
        slug: 'nursery-visit-checklist',
        bridgeCopy: '質問リストの次は、見学当日にその場で確認できるチェックリストで抜け漏れを防ぎましょう。',
      },
      {
        slug: 'nursery-cost-comparison',
        bridgeCopy: '見学ポイントが分かったら、次は認可・認可外の総コストを比較して費用面の不安を解消しましょう。',
      },
      {
        slug: 'nursery-unlicensed-demerit',
        bridgeCopy: '費用感がつかめたら、次は認可外保育園ならではの注意点・デメリットを事前に確認しておきましょう。',
      },
      {
        slug: 'nursery-fee-exemption-unlicensed',
        bridgeCopy: '注意点を踏まえたら、次は無償化補助の申請条件を確認して実質負担額を把握しましょう。',
      },
      {
        slug: 'hoikuen-erabikata',
        bridgeCopy: '最後は、ここまでの情報を家庭タイプ別の判断基準に落とし込み、園選びを決め切りましょう。',
      },
    ],
  },
  'home-monte-intro': {
    id: 'home-monte-intro',
    name: 'おうちモンテ入門コース',
    description: 'モンテッソーリ教育の基本の考え方から、おうちで実践する土台づくりまでを順番に理解できます。',
    kicker: '考え方から関わり方まで、体系的に',
    nextCourseId: 'age2-jibunde',
    steps: [
      {
        slug: 'about-montessori',
        bridgeCopy: 'まずはモンテッソーリ教育がどんな考え方なのか、全体像から押さえましょう。',
      },
      {
        slug: 'sensitive-periods',
        bridgeCopy: '全体像がつかめたら、次は関わり方の土台となる「敏感期」の考え方を理解しましょう。',
      },
      {
        slug: 'concentration',
        bridgeCopy:
          '敏感期が分かったら、次は敏感期から生まれる「集中現象」を理解し、子どもの集中を邪魔しない関わり方を身につけましょう。',
      },
      {
        slug: 'install-home-monte',
        bridgeCopy: '集中現象を理解したら、次はその集中を引き出す環境を整える「おうちモンテ」の始め方に進みましょう。',
      },
      {
        slug: 'curiosity',
        bridgeCopy: '環境の整え方が分かったら、次は好奇心を伸ばす具体的な声かけの工夫を学びましょう。',
      },
      {
        slug: 'self_correction',
        bridgeCopy: '最後は、間違いを自分で訂正する力の育て方を知り、日々の関わり方を仕上げましょう。',
      },
    ],
  },
  'age2-jibunde': {
    id: 'age2-jibunde',
    name: '2歳・自分でやりたいコース',
    description: '2歳の「自分でやりたい」気持ちを、教具・遊び・イヤイヤ期への関わりから実践に落とし込みます。',
    kicker: '教具選びから自立心の育て方まで、体系的に',
    steps: [
      {
        slug: '2-years-old-equipment',
        bridgeCopy: '2歳の「自分でやりたい」気持ちを支える教具選びから始めましょう。',
      },
      {
        slug: 'indoor-play-2-years-old',
        bridgeCopy: '教具が揃ったら、次は家でできる室内遊びのバリエーションを増やしましょう。',
      },
      {
        slug: 'indoor-exercise-play-age-2',
        bridgeCopy: '室内遊びの幅が広がったら、次は体をたっぷり動かす遊びで運動の敏感期を満たしましょう。',
      },
      {
        slug: 'clay-play-ideas-age-2',
        bridgeCopy: '体を動かした次は、手先を使う粘土遊びで「自分でやりたい」気持ちをさらに伸ばしましょう。',
      },
      {
        slug: 'otetsudai-age-2',
        bridgeCopy: '遊びで手先が育ってきたら、次は「日常生活の練習」につながるお手伝いに挑戦してみましょう。',
      },
      {
        slug: 'iyaiya',
        bridgeCopy:
          'お手伝いで「自分で！」が増えてきたら、次はその裏にある気持ちを理解し、イヤイヤ期の関わり方を押さえましょう。',
      },
      {
        slug: 'independence',
        bridgeCopy: '最後は、イヤイヤ期の関わり方を踏まえて、自立心を育む声かけのコツを学びましょう。',
      },
    ],
  },
  'toilet-training': {
    id: 'toilet-training',
    name: 'トイトレ完走コース',
    description: '始め方から年齢別の進め方、うまくいかない時の対処、夜のトイトレまで順番に伴走します。',
    kicker: '始め方から夜のトイトレまで、体系的に',
    steps: [
      {
        slug: 'toilet-training',
        bridgeCopy: 'まずはトイトレ全体の進め方と5ステップの流れを押さえましょう。',
      },
      {
        slug: 'toilet-training-pants',
        bridgeCopy: '全体の流れが分かったら、次はトイトレパンツの選び方と切り替え時期を押さえて準備を整えましょう。',
      },
      {
        slug: 'toilet-training-age-2',
        bridgeCopy: 'パンツの準備ができたら、次は2歳・イヤイヤ期でも進めやすい始め方のコツを見ていきましょう。',
      },
      {
        slug: 'toilet-training-age-3',
        bridgeCopy: '2歳の進め方の次は、3歳でまだ取れていない場合の焦らない進め方を確認しましょう。',
      },
      {
        slug: 'toilet-training-not-working',
        bridgeCopy: '年齢別の目安が分かったら、次はうまくいかない時によくあるつまずきの原因と対処法を見ましょう。',
      },
      {
        slug: 'toilet-training-night',
        bridgeCopy: '最後は、日中と切り分けて考えるべき夜のトイトレ・おねしょ対策で仕上げましょう。',
      },
    ],
  },
  'sleep-bedtime': {
    id: 'sleep-bedtime',
    name: 'ねんね・寝かしつけコース',
    description: '新生児期から2〜3歳まで、月齢・年齢に合わせた寝かしつけの考え方を順番に整理します。',
    kicker: '夜泣きから寝かしつけの卒業まで、体系的に',
    steps: [
      {
        slug: 'newborn-bedtime',
        bridgeCopy: 'まずは新生児期の昼夜のリズムの整え方から、ねんねの土台を作りましょう。',
      },
      {
        slug: 'baby-night-crying',
        bridgeCopy: 'リズムが分かったら、次は月齢別の夜泣きの原因と安眠環境の整え方を見ていきましょう。',
      },
      {
        slug: 'self-settling-sleep',
        bridgeCopy: '夜泣き対策の次は、抱っこに頼りすぎずセルフねんねへ移る4ステップを確認しましょう。',
      },
      {
        slug: 'nap-refusal-age-2',
        bridgeCopy: '夜のねんねが整ってきたら、次は2歳前後で増える「昼寝しない」悩みへの考え方を整理しましょう。',
      },
      {
        slug: 'toddler-bedtime',
        bridgeCopy: '昼寝との付き合い方が分かったら、次は1〜3歳が夜寝ない理由と年齢別の環境づくりに進みましょう。',
      },
      {
        slug: 'bedtime-age-2',
        bridgeCopy: '最後は、イヤイヤ期特有の「寝るのを嫌がる」場面への具体的な対応で仕上げましょう。',
      },
    ],
  },
  'iyaiya-tantrum': {
    id: 'iyaiya-tantrum',
    name: 'イヤイヤ期・かんしゃくコース',
    description: '1歳台のイタズラから3〜4歳のかんしゃくまで、年齢の変化に合わせた関わり方を学べます。',
    kicker: 'イタズラからかんしゃくまで、体系的に',
    steps: [
      {
        slug: '2-year-old-prank',
        bridgeCopy: 'まずは1〜2歳の「イタズラ」に見える行動の意味を理解するところから始めましょう。',
      },
      {
        slug: 'tantrum-2-year-old',
        bridgeCopy: 'イタズラの意味が分かったら、次は2歳特有の癇癪の背景と原因別の対応を深く理解しましょう。',
      },
      {
        slug: 'tantrums-self-will',
        bridgeCopy: '2歳の癇癪が分かったら、次は思い通りにならない時の癇癪への年齢別の声かけを押さえましょう。',
      },
      {
        slug: 'tantrum-3-year-old',
        bridgeCopy: '年齢別の声かけが分かったら、次は「魔の三歳児」と呼ばれる3歳特有の癇癪への対処法を見ましょう。',
      },
      {
        slug: '4-year-old-tantrums',
        bridgeCopy: '最後は、4歳で癇癪がひどくなる理由と、秩序の敏感期を活かした感情サポートで仕上げましょう。',
      },
    ],
  },
  'baby-following': {
    id: 'baby-following',
    name: '赤ちゃんの後追い理解コース',
    description: '後追いが始まる時期から落ち着く目安、後追いをしない場合の考え方まで順番に整理します。',
    kicker: '始まる時期から個人差まで、体系的に',
    steps: [
      {
        slug: 'baby-following-when',
        bridgeCopy: 'まずは後追いが始まる月齢の目安と、発達上の背景を確認しましょう。',
      },
      {
        slug: 'baby-following-until',
        bridgeCopy: '始まる時期が分かったら、次は落ち着く時期の目安と日々の乗り切り方を見ていきましょう。',
      },
      {
        slug: 'baby-not-following',
        bridgeCopy: '最後は、後追いをしない場合の個人差と、相談を考えたいサインを整理しましょう。',
      },
    ],
  },
  'parent-care': {
    id: 'parent-care',
    name: '親の心ケアコース',
    description: 'イライラやしんどさを我慢するのではなく、原因を整理して相談先まで見つけられるコースです。',
    kicker: '原因整理から相談先まで、体系的に',
    steps: [
      {
        slug: 'parenting-iraira',
        bridgeCopy: 'まずは育児でイライラしてしまう原因を整理し、今日からできる解消法を知りましょう。',
      },
      {
        slug: 'parenting-tired',
        bridgeCopy: 'イライラの原因が分かったら、次は「疲れた」と感じる時の対処法に目を向けましょう。',
      },
      {
        slug: 'parenting-tsurai',
        bridgeCopy: '疲れへの対処が分かったら、次は「つらい」という感情そのものを軽くする視点を持ちましょう。',
      },
      {
        slug: 'older-sibling-feelings',
        bridgeCopy:
          '気持ちが少し軽くなったら、次はきょうだいができてから増えやすい「上の子への気持ち」にも目を向けてみましょう。',
      },
      {
        slug: 'parenting-neurosis',
        bridgeCopy:
          '上の子への気持ちを整理しても罪悪感が続く場合に備えて、次は育児ノイローゼのセルフチェックと相談目安も確認しておきましょう。',
      },
      {
        slug: 'parenting-consultation',
        bridgeCopy: '最後は、一人で抱え込まないために、悩み別の相談先の選び方を知っておきましょう。',
      },
    ],
  },
};

export const BLOG_COURSE_IDS: BlogCourseId[] = [
  'hokatsu-start',
  'nursery-choice',
  'home-monte-intro',
  'age2-jibunde',
  'toilet-training',
  'sleep-bedtime',
  'baby-following',
  'iyaiya-tantrum',
  'parent-care',
];
